// MBA Lab — Stakeholder Interview Builder UI. Imports only lib/core.js and
// lib/interview-model.js. Every question is server-rendered as static
// content (see Views/Tools/StakeholderInterviewBuilder.cshtml) so the full
// bank is readable and usable with JS disabled - this file adds selection,
// the live document preview, and copy/download/print on top, matching
// team-charter.js's precedent.
import { store, download, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import {
  SCHEMA_VERSION,
  LIMITS,
  createInitialState,
  parseSavedState,
  serializeForSave,
  setStakeholderName,
  setStakeholderRole,
  setPurpose,
  setLogistics,
  setCustomQuestions,
  toggleQuestion,
  isQuestionSelected,
  isSelectionFull,
  selectedQuestionCount,
  getDocumentModel,
  buildMarkdown,
} from './lib/interview-model.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'stakeholder-interview-builder';
const AUTOSAVE_DEBOUNCE_MS = 400;

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

class StakeholderInterviewBuilder {
  constructor() {
    this.cacheElements();
    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with
    // the feature off, this is a pure passthrough to the same localStorage wrapper.
    this.interviewStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.rafId = null;
    this.saveTimer = null;
    this.hasEngaged = false;
    this.dock = initToolDock({
      dock: byId('interview-dock'),
      jumpTarget: byId('interview-guide'),
      returnTarget: byId('interview-form'),
    });

    this.state = this.loadInitialState();
    this.applyStateToForm();
    this.renderDocument();
    this.renderSelectionCount();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.form = byId('interview-form');
    this.questionBank = byId('interview-questions');
    this.els = {
      stakeholderName: byId('interview-stakeholder-name'),
      stakeholderRole: byId('interview-stakeholder-role'),
      purpose: byId('interview-purpose'),
      logistics: byId('interview-logistics'),
      customQuestions: byId('interview-custom-questions'),
      selectionCount: byId('interview-selection-count'),
      docTitle: byId('interview-doc-title'),
      docSubtitle: byId('interview-doc-subtitle'),
      docPurpose: byId('interview-doc-purpose'),
      docSections: byId('interview-doc-sections'),
      copyBtn: byId('interview-copy'),
      downloadBtn: byId('interview-download'),
      printBtn: byId('interview-print'),
      actionFeedback: byId('interview-action-feedback'),
      startOver: byId('interview-start-over'),
    };
  }

  loadInitialState() {
    const saved = this.interviewStore.load();
    if (!saved) return createInitialState();
    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    return createInitialState();
  }

  applyStateToForm() {
    this.els.stakeholderName.value = this.state.stakeholderName;
    this.els.stakeholderRole.value = this.state.stakeholderRole;
    this.els.purpose.value = this.state.purpose;
    this.els.logistics.value = this.state.logistics;
    this.els.customQuestions.value = this.state.customQuestions;
    this.applyStateToQuestionBank();
  }

  applyStateToQuestionBank() {
    const full = isSelectionFull(this.state);
    for (const el of this.questionBank.querySelectorAll('[data-question-id]')) {
      const selected = isQuestionSelected(this.state, el.dataset.questionId);
      el.checked = selected;
      el.disabled = !selected && full;
    }
  }

  wireEvents() {
    this.form.addEventListener('input', (e) => this.handleFieldInput(e));
    this.questionBank.addEventListener('change', (e) => this.handleQuestionToggle(e));
    this.els.copyBtn.addEventListener('click', () => this.handleCopy());
    this.els.downloadBtn.addEventListener('click', () => this.handleDownload());
    this.els.printBtn.addEventListener('click', () => this.handlePrint());
    this.els.startOver.addEventListener('click', () => this.handleStartOver());
  }

  handleFieldInput(e) {
    const el = e.target;
    if (el === this.els.stakeholderName) this.state = setStakeholderName(this.state, el.value);
    else if (el === this.els.stakeholderRole) this.state = setStakeholderRole(this.state, el.value);
    else if (el === this.els.purpose) this.state = setPurpose(this.state, el.value);
    else if (el === this.els.logistics) this.state = setLogistics(this.state, el.value);
    else if (el === this.els.customQuestions) this.state = setCustomQuestions(this.state, el.value);
    else return;
    this.markEngaged();
    this.scheduleUpdate();
  }

  handleQuestionToggle(e) {
    const el = e.target.closest('[data-question-id]');
    if (!el || !this.questionBank.contains(el)) return;
    const wasSelected = isQuestionSelected(this.state, el.dataset.questionId);
    this.state = toggleQuestion(this.state, el.dataset.questionId);
    if (!wasSelected && !isQuestionSelected(this.state, el.dataset.questionId)) {
      el.checked = false; // cap reached - revert the checkbox click
      return;
    }
    this.markEngaged();
    this.applyStateToQuestionBank();
    this.scheduleUpdate();
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  scheduleUpdate() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.renderDocument();
      this.renderSelectionCount();
      this.scheduleAutosave();
    });
  }

  renderSelectionCount() {
    const count = selectedQuestionCount(this.state);
    this.els.selectionCount.textContent = `${count} of ${LIMITS.maxSelectedQuestions} questions selected`;
    this.dock?.setReading(`${count} of ${LIMITS.maxSelectedQuestions} questions selected`);
    this.dock?.setComplete(count > 0);
  }

  renderDocument() {
    const doc = getDocumentModel(this.state);

    this.els.docTitle.textContent = doc.stakeholderName || '[Stakeholder name]';
    const roleAndLogistics = [doc.stakeholderRole, doc.logistics].filter(Boolean).join(' — ');
    this.els.docSubtitle.textContent = roleAndLogistics || '[Role] — [Time & setting]';
    this.els.docPurpose.textContent = doc.purpose || '[Add the purpose of this interview]';

    clearChildren(this.els.docSections);
    if (!doc.sections.length) {
      const empty = document.createElement('p');
      empty.className = 'tool-snapshot-incomplete-note';
      empty.textContent = 'Select a question below, or add your own, to build the guide.';
      this.els.docSections.appendChild(empty);
      return;
    }

    for (const section of doc.sections) {
      const wrap = document.createElement('div');
      wrap.className = 'tool-interview-doc-section';

      // h4, not h3: the document's own title (#interview-doc-title) is h3,
      // so each goal section one level below it is h4 - matches the CSS,
      // which already visually styles this as the same small-caps tier as
      // the "Purpose" h4 label above it (audit finding: this used to be an
      // h3 here, skipping a level between it and the h3 title right above).
      const title = document.createElement('h4');
      title.textContent = section.title;
      wrap.appendChild(title);

      for (const block of section.blocks) {
        if (block.type !== 'list') continue;
        const ul = document.createElement('ul');
        for (const item of block.items) {
          const li = document.createElement('li');
          li.textContent = item;
          ul.appendChild(li);
        }
        wrap.appendChild(ul);
      }

      this.els.docSections.appendChild(wrap);
    }
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) return;
    this.interviewStore.save(toSave);
  }

  /** Audit finding: this tool holds real free-text (stakeholder name,
   * purpose, custom questions) in localStorage indefinitely with no way to
   * clear it - the privacy note already promised "unless you clear it"
   * with nothing behind that promise. Matches org-alignment-workbench.js's
   * handleStartOver() exactly. */
  handleStartOver() {
    this.interviewStore.deleteAccountState();
    this.state = createInitialState();
    this.form.reset();
    this.applyStateToQuestionBank();
    this.renderDocument();
    this.renderSelectionCount();
  }

  handlePrint() {
    trackToolAction(TOOL_NAME, 'print');
    this.showActionFeedback('Opening print…');
    window.print();
  }

  handleDownload() {
    const markdown = buildMarkdown(this.state);
    const slug = (this.state.stakeholderName || 'stakeholder-interview')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '') || 'stakeholder-interview';
    download(`${slug}-interview-guide.md`, 'text/markdown', markdown);
    this.showActionFeedback('Downloaded.');
    trackToolAction(TOOL_NAME, 'download');
  }

  async handleCopy() {
    const markdown = buildMarkdown(this.state);
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(markdown);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) copied = this.fallbackCopy(markdown);

    this.showActionFeedback(copied ? 'Copied!' : 'Could not copy - try downloading instead.');
    if (copied) trackToolAction(TOOL_NAME, 'copy');
  }

  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  showActionFeedback(message) {
    this.els.actionFeedback.textContent = message;
    setTimeout(() => {
      if (this.els.actionFeedback.textContent === message) this.els.actionFeedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('interview-form')) return;
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads
  // whatever won. A no-op (and no request) when signed out, and bounded by its own short
  // timeout so a slow network delays startup rather than blocking it.
  await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  new StakeholderInterviewBuilder();
});
